import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthUserDto, LoginRequestDto, ModuleKey, PermissionMap } from '@gty/common';

import { ApiError } from '../api/errors';
import * as endpoints from '../api/endpoints';
import { refreshAccessToken } from '../api/refresh-interceptor';
import { DEV_AUTH_ENABLED, devLogin } from './dev-auth';
import {
  clearAccessToken,
  notifySessionEnded,
  onSessionEnded,
  setAccessToken,
  type SessionEndReason,
} from '../api/token-store';

/**
 * Session state for the whole app.
 *
 * `booting` is a distinct state on purpose. On a reload the access token is
 * gone (it only ever lived in memory), so the app tries ONE silent refresh
 * before it renders anything. Without a `booting` state the guards would see
 * "no user", bounce to /login, and only then discover the session was fine —
 * a login-page flash on every refresh.
 */
export type AuthStatus = 'booting' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUserDto | null;
  /** Effective level per module. A missing key means NONE (spec §5.3 step 4). */
  permissions: PermissionMap;
  /** Set when a session ended by itself, so /login can explain why. */
  sessionNotice: string | null;
  login: (credentials: LoginRequestDto) => Promise<void>;
  logout: () => Promise<void>;
  clearSessionNotice: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

const NOTICE: Readonly<Record<SessionEndReason, string | null>> = Object.freeze({
  FORCE_REAUTH: 'Your session was ended for security reasons. Please sign in again.',
  REFRESH_FAILED: 'Your session expired. Please sign in again.',
  // A deliberate logout needs no explanation.
  LOGOUT: null,
});

function toPermissionMap(entries: ReadonlyArray<{ module: ModuleKey; level: PermissionMap[ModuleKey] }>): PermissionMap {
  const map: PermissionMap = {};
  for (const entry of entries) {
    map[entry.module] = entry.level;
  }
  return map;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('booting');
  const [user, setUser] = useState<AuthUserDto | null>(null);
  const [permissions, setPermissions] = useState<PermissionMap>({});
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);

  // StrictMode double-invokes effects in development. Two boot refreshes would
  // present the same rotating refresh token twice and trip the server's reuse
  // detector — logging the developer out on every page load.
  const bootStarted = useRef(false);

  const queryClient = useQueryClient();

  const resetSession = useCallback(() => {
    clearAccessToken();
    setUser(null);
    setPermissions({});
    setStatus('anonymous');
    /**
     * WIPE THE CACHE ON EVERY SESSION END, not just an explicit logout.
     *
     * react-query's client lives above this provider (`app/providers.tsx`) and
     * survives a logout/login cycle by construction — nothing else ever told
     * it a different principal is now asking. `CACHE.search` alone keeps
     * results warm for 30s; without this, a shared workstation lets the next
     * signed-in user see the previous user's permission-scoped search rows
     * for up to that long, served straight from cache with no new request to
     * gate. Every `onSessionEnded` reason (logout, forced reauth, refresh
     * failure) routes through here, so this closes all three at once.
     */
    void queryClient.clear();
  }, [queryClient]);

  const loadMe = useCallback(async () => {
    const me = await endpoints.fetchMe();
    setUser(me.user);
    setPermissions(toPermissionMap(me.permissions));
    setStatus('authenticated');
  }, []);

  // Boot: one silent refresh, then /auth/me. Any failure just means "not
  // signed in" — it is the normal first-visit path, not an error worth showing.
  useEffect(() => {
    if (bootStarted.current) return;
    bootStarted.current = true;

    // With the dev stub on there is no API to refresh against; skip straight
    // to anonymous so the login form appears without a wasted failing request.
    if (DEV_AUTH_ENABLED) {
      setStatus('anonymous');
      return;
    }

    /**
     * NO `cancelled` FLAG HERE — it deadlocked the app, and re-adding one will
     * deadlock it again.
     *
     * `bootStarted` already guarantees this runs exactly once per provider. Pair
     * that with a cleanup that sets `cancelled = true` and StrictMode's
     * mount/unmount/remount produces: boot starts -> cleanup marks it cancelled
     * -> the second mount returns early on the ref -> the original promise
     * settles and every `setStatus` is skipped. `status` stays 'booting'
     * forever and the app renders nothing but a spinner.
     *
     * Settling state here after a simulated unmount is correct and safe: this
     * provider wraps the whole app so it is never really unmounted, and React 18
     * dropped the "setState on an unmounted component" warning precisely because
     * guarding against it caused bugs like this one.
     */
    void (async () => {
      try {
        await refreshAccessToken();
        await loadMe();
      } catch {
        clearAccessToken();
        setStatus('anonymous');
      }
    })();
  }, [loadMe]);

  // The api layer ends the session from outside React (a 401 on any request).
  // This is the bridge back into component state.
  useEffect(
    () =>
      onSessionEnded((reason) => {
        // Boot failures are not "your session ended" — suppress the notice
        // until the user has actually been signed in at least once.
        setSessionNotice((current) => (status === 'authenticated' ? NOTICE[reason] : current));
        resetSession();
      }),
    [resetSession, status],
  );

  const login = useCallback(
    async (credentials: LoginRequestDto) => {
      if (DEV_AUTH_ENABLED) {
        const session = devLogin(credentials.identifier ?? credentials.email ?? '', credentials.password);
        if (session === null) {
          // Reuse the real failure path so the form's error handling is the
          // same code that will run against the live endpoint.
          throw new ApiError(401, 'Incorrect email or password.', 'INVALID_CREDENTIALS', null);
        }
        setUser(session.user);
        setPermissions(session.permissions);
        setSessionNotice(null);
        setStatus('authenticated');
        return;
      }

      const response = await endpoints.login(credentials);
      setAccessToken(response.accessToken);
      setSessionNotice(null);
      // /auth/me is the ONLY source of the permission map — the login response
      // deliberately does not carry one (spec §5.3: permissions are never in
      // the token, and never cached past a role edit).
      await loadMe();
    },
    [loadMe],
  );

  const logout = useCallback(async () => {
    if (DEV_AUTH_ENABLED) {
      notifySessionEnded('LOGOUT');
      return;
    }
    try {
      await endpoints.logout();
    } catch {
      // A failed logout call must still clear the client. The refresh cookie
      // may already be dead; leaving the UI "signed in" would be worse.
    } finally {
      notifySessionEnded('LOGOUT');
    }
  }, []);

  const clearSessionNotice = useCallback(() => {
    setSessionNotice(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, permissions, sessionNotice, login, logout, clearSessionNotice }),
    [status, user, permissions, sessionNotice, login, logout, clearSessionNotice],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
